import { AxiosInstance } from 'axios';
import type { components, operations } from '@/api/generated/openapi';
import { axiosWithoutCaseMiddleware } from '@/helpers/forms/axios';

export const validAdjustmentReasons = ['lost', 'found', 'damaged', 'other'];

export interface adjustmentInterface {
    uuid: string
    notes?: string | null
    reason: string
}

export interface CreatedAdjustment {
    uuid: string
    createdAt: string
    reason: string
    notes: string | null
    warehouseUuid: string
    moves: {
        uuid: string
        quantity: number
        status: number
        completedAt: string | null
    }[]
}

export type AdjustmentRaw = components['schemas']['Adjustment'];
export type FetchAdjustmentResponse = operations['get-adjustment']['responses']['200']['content']['application/json'];

export type CreateAdjustmentRequest = operations['create-adjustment']['requestBody']['content']['application/json'];
export type CreateAdjustmentResponse = operations['create-adjustment']['responses']['200']['content']['application/json'];
export type CreatedAdjustmentRaw = CreateAdjustmentResponse['adjustment'];

export function toAdjustment(raw: AdjustmentRaw): adjustmentInterface {
    return {
        uuid: raw.uuid,
        notes: raw.notes,
        reason: raw.reason,
    };
}

export function toCreatedAdjustment(raw: CreatedAdjustmentRaw): CreatedAdjustment {
    return {
        uuid: raw.uuid,
        createdAt: raw.created_at,
        reason: raw.reason,
        notes: raw.notes,
        warehouseUuid: raw.warehouse_uuid,
        moves: raw.moves.map(move => ({
            uuid: move.uuid,
            quantity: move.quantity,
            status: move.status,
            completedAt: move.completed_at,
        })),
    };
}

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): {
    fetchAdjustment: (uuid: string) => Promise<adjustmentInterface>;
    createAdjustment: (body: CreateAdjustmentRequest) => Promise<CreatedAdjustment>;
} => ({
    fetchAdjustment: async (uuid: string): Promise<adjustmentInterface> => {
        const response = await axios.get<FetchAdjustmentResponse>(`/api/adjustments/${uuid}`);

        return toAdjustment(response.data);
    },
    createAdjustment: async (body: CreateAdjustmentRequest): Promise<CreatedAdjustment> => {
        const response = await axios.post<CreateAdjustmentResponse>('/api/adjustments', body);

        return toCreatedAdjustment(response.data.adjustment);
    },
});
